import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { fetchContent } from "@/lib/api";
import LockScreen from "@/components/LockScreen";
import MenuBar from "@/components/MenuBar";
import Dock from "@/components/Dock";
import Window from "@/components/Window";
import DesktopNotification from "@/components/DesktopNotification";
import CalendarWindow from "@/components/windows/CalendarWindow";
import NotesWindow from "@/components/windows/NotesWindow";
import SafariWindow from "@/components/windows/SafariWindow";
import ThreadsLogo from "@/components/ThreadsLogo";
import { SubstackSoon, ThreadsSoon } from "@/components/windows/ComingSoonWindow";
import { XIcon, InstagramIcon, LinkedInIcon, SubstackIcon, VenmoIcon, PayPalIcon, CashAppIcon } from "@/components/BrandIcons";
import { CalendarDays, StickyNote, Compass, Mail, Globe } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOW_DEFS = {
  calendar: { title: "Calendar", component: CalendarWindow, size: { w: 640, h: 480 } },
  notes: { title: "Notes", component: NotesWindow, size: { w: 460, h: 520 } },
  safari: { title: "Safari", component: SafariWindow, size: { w: 820, h: 560 } },
  substack: { title: "Substack", component: SubstackSoon, size: { w: 440, h: 540 } },
  threads: { title: "September Threads", component: ThreadsSoon, size: { w: 440, h: 540 } },
};

function PlaceholderPopup({ label, onClose, zIndex, onFocus }) {
  return (
    <Window title={label} onClose={onClose} zIndex={zIndex} onFocus={onFocus}
      initialSize={{ w: 380, h: 200 }} initialPos={{ x: 120, y: 120 }}>
      <div className="h-full flex flex-col items-center justify-center gap-2 p-6 text-center">
        <Globe size={32} className="text-gray-400" />
        <div className="text-sm font-semibold text-gray-700">{label} link coming soon</div>
        <div className="text-xs text-gray-500">Cory hasn't added this link yet. Check back soon.</div>
      </div>
    </Window>
  );
}

export default function Desktop() {
  const [content, setContent] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [windows, setWindows] = useState([]); // [{id, z}]
  const [zCounter, setZCounter] = useState(100);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);

  const handleUnlock = useCallback(() => {
    setUnlocked(true);
    // Unlock is a user gesture, so audio may start here
    if (audioRef.current) {
      audioRef.current.volume = 0.45;
      audioRef.current.play().catch(() => {});
    }
  }, []);

  useEffect(() => {
    fetchContent().then(setContent).catch(() => setContent({}));
  }, []);

  const openWindow = useCallback((id) => {
    setZCounter((z) => {
      setWindows((ws) => {
        if (ws.find((w) => w.id === id)) {
          return ws.map((w) => (w.id === id ? { ...w, z: z + 1 } : w));
        }
        return [...ws, { id, z: z + 1 }];
      });
      return z + 1;
    });
  }, []);

  const closeWindow = useCallback((id) => {
    setWindows((ws) => ws.filter((w) => w.id !== id));
  }, []);

  const openApp = useCallback((app) => {
    if (app.action === "window") return openWindow(app.id);
    if (app.action === "link" && app.url) return window.open(app.url, "_blank", "noopener");
    if (app.action === "mailto" && app.url) return (window.location.href = app.url);
    if (app.action === "placeholder") return openWindow(app.id);
  }, [openWindow]);

  const dockApps = useMemo(() => {
    if (!content) return [];
    const L = content.links || {};
    const placeholder = (id, label, icon, bg) => ({ id, label, icon, bg, action: "placeholder" });
    const link = (id, label, icon, bg, url) =>
      url ? { id, label, icon, bg, action: "link", url } : placeholder(id, label, icon, bg);
    const soon = (id, label, icon, bg, url) =>
      url ? { id, label, icon, bg, action: "link", url } : { id, label, icon, bg, action: "window" };
    // Left-to-right: Email, Notes, CashApp, Venmo, PayPal, Calendar, Safari, socials, Substack, September Threads
    return [
      { id: "mail", label: "Mail", icon: <Mail size={26} className="text-white" />, bg: "linear-gradient(160deg,#6fb7ff,#0a6cff)", action: "mailto", url: `mailto:${content.mail_email || "whatsup@corysuggs.com"}` },
      { id: "divider-1", divider: true },
      { id: "notes", label: "Notes", icon: <StickyNote size={26} className="text-yellow-800" />, bg: "linear-gradient(160deg,#ffe98a,#f7c948)", action: "window" },
      link("cashapp", "Cash App", <CashAppIcon size={24} className="text-white" />, "#00d64f", L.cashapp),
      link("venmo", "Venmo", <VenmoIcon size={22} className="text-white" />, "#008cff", L.venmo),
      link("paypal", "PayPal", <PayPalIcon size={24} className="text-white" />, "#003087", L.paypal),
      { id: "calendar", label: "Calendar", icon: <CalendarDays size={26} className="text-white" />, bg: "linear-gradient(160deg,#ff6b6b,#d92b2b)", action: "window" },
      { id: "safari", label: "Safari", icon: <Compass size={26} className="text-white" />, bg: "linear-gradient(160deg,#5ac8fa,#0a84ff)", action: "window" },
      link("instagram", "Instagram", <InstagramIcon size={24} className="text-white" />, "linear-gradient(45deg,#f9ce34,#ee2a7b,#6228d7)", L.instagram),
      link("x", "X", <img src="/x-logo.png" alt="X" className="w-8 h-8 rounded-[6px]" />, "#000000", L.x),
      link("linkedin", "LinkedIn", <LinkedInIcon size={24} className="text-white" />, "#0a66c2", L.linkedin),
      soon("substack", "Substack", <SubstackIcon size={24} className="text-white" />, "#ff6719", L.substack),
      soon("threads", "September Threads", <ThreadsLogo size={30} />, "rgba(255,255,255,0.18)", L.threads),
    ];
  }, [content]);

  if (!content) return <div className="h-full bg-black" />;

  const wallpaperUrl = content.wallpaper_url
    ? `${BACKEND_URL}${content.wallpaper_url}`
    : "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?q=80&w=1920&auto=format&fit=crop";
  // H.264 fallback for browsers without VP9 (rare) — Safari plays mp4, Chrome plays webm
  const wallpaperFallback = `${BACKEND_URL}/api/files/70ccf066-e066-4bf0-a9bb-fde69139a008`;

  const activeWindowTitle = windows.length
    ? WINDOW_DEFS[windows[windows.length - 1].id]?.title || dockApps.find((a) => a.id === windows[windows.length - 1].id)?.label
    : null;

  return (
    <div className="h-full w-full relative overflow-hidden bg-black" data-testid="desktop">
      {/* Wallpaper */}
      {content.wallpaper_type === "video" && content.wallpaper_url ? (
        <>
          <video autoPlay muted loop playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(1) brightness(0.55) contrast(1.05)" }}>
            <source src={wallpaperUrl} type="video/webm" />
            <source src={wallpaperFallback} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/45" />
        </>
      ) : (
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${wallpaperUrl})` }} />
      )}

      <MenuBar activeApp={activeWindowTitle} muted={muted} onToggleMute={content.music_url ? () => {
        setMuted((m) => {
          if (audioRef.current) audioRef.current.muted = !m;
          return !m;
        });
      } : undefined} />
      <div className="hidden md:block">
        <DesktopNotification onOpenNotes={() => openWindow("notes")} />
      </div>

      {/* Desktop label */}
      <div className="absolute top-12 left-5 z-[40] text-white/90 select-none pointer-events-none">
        <div className="text-3xl md:text-5xl font-bold tracking-tight drop-shadow-lg" data-testid="desktop-name">corysuggs</div>
        <div className="text-sm md:text-base italic opacity-80 drop-shadow" data-testid="desktop-tagline">{content.tagline}</div>
      </div>

      {/* Windows */}
      {windows.map((w, i) => {
        const def = WINDOW_DEFS[w.id];
        const pos = { x: 40 + i * 32, y: 48 + i * 28 };
        if (def) {
          const Comp = def.component;
          return (
            <Window key={w.id} title={def.title} zIndex={w.z} onClose={() => closeWindow(w.id)}
              onFocus={() => openWindow(w.id)} initialSize={def.size} initialPos={pos}>
              <Comp url={w.id === "safari" ? content.links?.safari : undefined} />
            </Window>
          );
        }
        const app = dockApps.find((a) => a.id === w.id);
        return (
          <PlaceholderPopup key={w.id} label={app?.label || "Link"} zIndex={w.z}
            onClose={() => closeWindow(w.id)} onFocus={() => openWindow(w.id)} />
        );
      })}

      <Dock apps={dockApps} openWindows={windows.map((w) => w.id)} onOpen={openApp} />

      {!unlocked && <LockScreen content={content} onUnlock={handleUnlock} />}

      {content.music_url && (
        <audio ref={audioRef} src={`${BACKEND_URL}${content.music_url}`} loop preload="auto" data-testid="bg-music" />
      )}
    </div>
  );
}
